import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { paymentAPI } from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Loader2, CreditCard, CheckCircle, XCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const PaymentFlow = ({ ride, onPaymentComplete }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  // Check for payment return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentResult = searchParams.get('payment');

    if (sessionId && paymentResult === 'success') {
      checkPaymentStatus(sessionId);
    } else if (paymentResult === 'cancelled') {
      setPaymentStatus('cancelled');
      toast.error('Payment was cancelled');
      // Clear URL params
      navigate(window.location.pathname, { replace: true });
    }
  }, [searchParams, navigate]);

  const checkPaymentStatus = async (sessionId, attempt = 0) => {
    setCheckingStatus(true);
    const maxAttempts = 5;
    const pollInterval = 2000;

    try {
      const response = await paymentAPI.getStatus(sessionId);
      const { payment_status, status } = response.data;

      if (payment_status === 'paid') {
        setPaymentStatus('success');
        toast.success('Payment successful!');
        if (onPaymentComplete) {
          onPaymentComplete();
        }
        // Clear URL params
        navigate(window.location.pathname, { replace: true });
        return;
      }

      if (status === 'expired') {
        setPaymentStatus('expired');
        toast.error('Payment session expired');
        navigate(window.location.pathname, { replace: true });
        return;
      }

      // Continue polling if still pending
      if (attempt < maxAttempts) {
        setTimeout(() => checkPaymentStatus(sessionId, attempt + 1), pollInterval);
      } else {
        setPaymentStatus('timeout');
        toast.info('Payment is still processing. Check your email for confirmation.');
        navigate(window.location.pathname, { replace: true });
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempt < maxAttempts) {
        setTimeout(() => checkPaymentStatus(sessionId, attempt + 1), pollInterval);
      }
    } finally {
      if (attempt >= maxAttempts - 1) {
        setCheckingStatus(false);
      }
    }
  };

  const initiatePayment = async () => {
    setProcessing(true);
    try {
      const response = await paymentAPI.createCheckout(ride.id);
      const { checkout_url } = response.data;
      
      // Redirect to Stripe Checkout
      window.location.href = checkout_url;
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setProcessing(false);
    }
  };

  // If checking status after return from Stripe
  if (checkingStatus) {
    return (
      <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
        <CardContent className="p-6 text-center">
          <Loader2 className="w-12 h-12 animate-spin text-red-600 mx-auto mb-4" />
          <h3 className="font-semibold text-lg text-slate-900 dark:text-white mb-2">
            Processing Payment...
          </h3>
          <p className="text-slate-500 dark:text-slate-400 text-sm">
            Please wait while we confirm your payment
          </p>
        </CardContent>
      </Card>
    );
  }

  // Payment success state
  if (paymentStatus === 'success') {
    return (
      <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
        <CardContent className="p-6 text-center">
          <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
          <h3 className="font-semibold text-lg text-green-800 dark:text-green-300 mb-2">
            Payment Successful!
          </h3>
          <p className="text-green-600 dark:text-green-400 text-sm">
            Thank you for riding with Reddi
          </p>
        </CardContent>
      </Card>
    );
  }

  // Payment cancelled/expired state
  if (paymentStatus === 'cancelled' || paymentStatus === 'expired') {
    return (
      <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
        <CardContent className="p-6 text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="font-semibold text-lg text-slate-900 dark:text-white mb-2">
            {paymentStatus === 'cancelled' ? 'Payment Cancelled' : 'Session Expired'}
          </h3>
          <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">
            {paymentStatus === 'cancelled' 
              ? 'You can try again whenever you\'re ready'
              : 'Please initiate a new payment'}
          </p>
          <Button
            onClick={initiatePayment}
            disabled={processing}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Check if ride is already paid
  const isPaid = ride.payment_status === 'paid' || ride.payment_status === 'completed';

  if (isPaid) {
    return (
      <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
        <CardContent className="p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-green-700 dark:text-green-400 font-medium">
            Payment completed
          </span>
        </CardContent>
      </Card>
    );
  }

  // Default payment button
  const totalAmount = (ride.actual_fare || ride.estimated_fare) + (ride.tip_amount || 0);

  return (
    <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
      <CardHeader>
        <CardTitle className="font-display uppercase tracking-tight flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-red-600" />
          Payment
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between mb-4">
          <span className="text-slate-500 dark:text-slate-400">Total Amount</span>
          <span className="text-2xl font-display font-bold text-slate-900 dark:text-white">
            ${totalAmount.toFixed(2)}
          </span>
        </div>
        
        <Button
          onClick={initiatePayment}
          disabled={processing}
          className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-full"
          data-testid="pay-now-btn"
        >
          {processing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Redirecting to payment...
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5 mr-2" />
              Pay Now
              <ExternalLink className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
        
        <p className="text-xs text-center text-slate-400 mt-3">
          Secure payment powered by Stripe
        </p>
      </CardContent>
    </Card>
  );
};

export default PaymentFlow;
